import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import {
  Card, CardHeader, CardTitle, CardContent,
  Badge, Button,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@agion/ui'
import { ScrollText, RefreshCw, Zap, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/core/analytics_utils'
import { queryLogs, logLabelValues } from '@/core/observability_api_client'
import type { LogEntry, LokiStream } from '@/core/observability_types'
import { LogAnalysisDialog } from './LogAnalysisDialog'
import { buildLogQueries, formatLogQueries, LOG_LEVEL_OPTIONS, parseLogLevel, type LevelFilter } from './log_query'

function streamsToEntries(streams: LokiStream[]): LogEntry[] {
  const entriesByKey = new Map<string, LogEntry>()
  for (const stream of streams) {
    for (const [ts, line] of stream.values) {
      const labelsKey = Object.entries(stream.stream)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join('\u0000')
      const key = `${ts}\u0000${line}\u0000${labelsKey}`
      entriesByKey.set(key, {
        timestamp: new Date(Number(ts) / 1e6),
        line,
        labels: stream.stream,
        level: parseLogLevel(line, stream.stream),
      })
    }
  }
  return [...entriesByKey.values()].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
}

const LEVEL_COLORS: Record<LogEntry['level'], string> = {
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warn: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  debug: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-400',
  unknown: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-400',
}

function normalizeLevel(value: string | null): LevelFilter {
  return LOG_LEVEL_OPTIONS.includes(value as LevelFilter) ? value as LevelFilter : 'all'
}

export const LogsPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const [searchParams, setSearchParams] = useSearchParams()
  const [entries, setEntries] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [services, setServices] = useState<string[]>([])
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [analyzeEntry, setAnalyzeEntry] = useState<LogEntry | null>(null)
  const [queryWarning, setQueryWarning] = useState<string | null>(null)

  const selectedService = searchParams.get('service') || '__all__'
  const levelFilter = normalizeLevel(searchParams.get('level'))
  const routeFilter = searchParams.get('route') || ''
  const activeQueries = useMemo(
    () => buildLogQueries(selectedService, levelFilter, routeFilter),
    [selectedService, levelFilter, routeFilter]
  )
  const activeQuery = useMemo(() => formatLogQueries(activeQueries), [activeQueries])

  // Fetch available services
  useEffect(() => {
    logLabelValues('app')
      .then(res => {
        if (res.status === 'success' && res.data) {
          setServices(res.data.filter(s => s.includes('agion') || s.includes('service') || s.includes('ui')))
        }
      })
      .catch(() => {})
  }, [])

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    setQueryError(null)
    setQueryWarning(null)
    try {
      const now = Math.floor(Date.now() / 1e3)
      const results = await Promise.allSettled(
        activeQueries.map(query => queryLogs(query, String(now - 3600), String(now), 200))
      )
      const streams: LokiStream[] = []
      const failures: string[] = []

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failures.push(result.reason instanceof Error ? result.reason.message : String(result.reason))
          return
        }
        if (result.value.status === 'error') {
          failures.push(activeQueries[index] ?? 'log query')
          return
        }
        streams.push(...(result.value.data?.result || []))
      })

      if (failures.length > 0 && streams.length === 0) {
        setEntries([])
        setQueryError(failures.join('; ') || t('observability.logQueryFailedStatus', 'Log query failed. The observability backend returned an error response.'))
        return
      }
      if (failures.length > 0) {
        setQueryWarning(t('observability.partialLogQueryFailed', 'Some log queries failed; showing partial results.'))
      }
      setEntries(streamsToEntries(streams))
    } catch (error) {
      setEntries([])
      setQueryError(error instanceof Error
        ? error.message
        : t('observability.logQueryFailed', 'Log query failed. Check the observability endpoint and try again.'))
    } finally {
      setLoading(false)
    }
  }, [activeQueries, t])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const displayedEntries = levelFilter === 'all'
    ? entries
    : entries.filter(entry => entry.level === levelFilter)

  const levelCounts = entries.reduce((acc, e) => {
    acc[e.level] = (acc[e.level] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const updateFilters = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(updates)) {
      if (!value || value === '__all__' || value === 'all') {
        next.delete(key)
      } else {
        next.set(key, value)
      }
    }
    setExpandedIndex(null)
    setSearchParams(next)
  }

  const clearFilters = () => {
    setExpandedIndex(null)
    setSearchParams({})
  }

  const hasActiveFilters = selectedService !== '__all__' || levelFilter !== 'all' || routeFilter !== ''

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <ScrollText className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          <div>
            <h1 className="page-title">{t('observability.logsTitle', 'Logs')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('observability.logsDescription', 'Search and explore logs from all platform services')}
            </p>
          </div>
        </div>
      </div>

      {/* Service selector + refresh */}
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex gap-3 items-center">
            <div className="w-64">
              <Select
                value={selectedService}
                onValueChange={(service: string) => updateFilters({ service, route: null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('observability.selectService', 'Select service')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">
                    {t('observability.allServices', 'All Services')}
                  </SelectItem>
                  {services.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchLogs} variant="outline" className="ml-auto">
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('observability.search', 'Search')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Level filter pills */}
      <div className="flex gap-2 mb-4">
        {LOG_LEVEL_OPTIONS.map(level => (
          <Button
            key={level}
            variant="ghost"
            onClick={() => updateFilters({ level })}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium h-auto',
              levelFilter === level
                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
            )}
          >
            {level === 'all' ? t('observability.all', 'All') : level.toUpperCase()}
            {level !== 'all' && levelCounts[level] ? ` (${levelCounts[level]})` : ''}
          </Button>
        ))}
      </div>

      <div className="mb-4 rounded-md border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800 dark:text-slate-100">
              {t('observability.activeFilters', 'Active filters')}:
            </span>
            {selectedService !== '__all__' && (
              <Badge variant="outline">{t('observability.filterService', 'Service')}: {selectedService}</Badge>
            )}
            {levelFilter !== 'all' && (
              <Badge variant="outline">{t('observability.filterLevel', 'Level')}: {levelFilter}</Badge>
            )}
            {routeFilter && (
              <Badge variant="outline">{t('observability.filterRoute', 'Route')}: {routeFilter}</Badge>
            )}
            <Button variant="ghost" className="h-7 px-2 text-xs" onClick={clearFilters}>
              {t('observability.clearFilters', 'Clear filters')}
            </Button>
          </div>
        )}
        <div className={cn('break-all font-mono text-[11px] text-slate-500 dark:text-slate-400', hasActiveFilters && 'mt-2')}>
          {t('observability.currentQuery', 'Current query')}: {activeQuery}
        </div>
        {queryWarning && (
          <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            {queryWarning}
          </div>
        )}
      </div>

      {/* Log entries */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span>{t('observability.logEntries', 'Log Entries')}</span>
            <Badge variant="default">{displayedEntries.length} {t('observability.results', 'results')}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-6 mb-2 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
              ))}
            </div>
          ) : queryError ? (
            <div className="space-y-3 p-12 text-center text-sm text-red-600 dark:text-red-300">
              <p className="font-medium">
                {t('observability.logQueryFailed', 'Log query failed. Check the observability endpoint and try again.')}
              </p>
              <p className="mx-auto max-w-2xl break-all text-xs text-red-500 dark:text-red-300">
                {queryError}
              </p>
              <p className="mx-auto max-w-2xl break-all font-mono text-[11px] text-slate-500 dark:text-slate-400">
                {activeQuery}
              </p>
              <Button variant="outline" className="h-8 text-xs" onClick={fetchLogs}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                {t('observability.retry', 'Retry')}
              </Button>
            </div>
          ) : displayedEntries.length === 0 ? (
            <div className="space-y-3 p-12 text-center text-sm text-slate-400">
              <p>{t('observability.noLogs', 'No log entries found for this query and time range.')}</p>
              <p className="mx-auto max-w-2xl break-all font-mono text-[11px] text-slate-500 dark:text-slate-400">
                {activeQuery}
              </p>
              {routeFilter && (
                <p className="mx-auto max-w-2xl text-xs text-slate-500 dark:text-slate-400">
                  {t('observability.routeLogHint', 'Gateway metrics may not include the route name in application logs. Keep this query as evidence, then inspect the service or gateway route that owns the failing request.')}
                </p>
              )}
              {hasActiveFilters && (
                <Button variant="outline" className="h-8 text-xs" onClick={clearFilters}>
                  {t('observability.clearFilters', 'Clear filters')}
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700/50 max-h-[600px] overflow-y-auto">
              {displayedEntries.map((entry, i) => {
                const isExpanded = expandedIndex === i
                return (
                  <div key={i}>
                    <div
                      role="button"
                      tabIndex={0}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors cursor-pointer"
                      onClick={() => setExpandedIndex(isExpanded ? null : i)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedIndex(isExpanded ? null : i) } }}
                    >
                      <div className="flex items-start gap-3">
                        {isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                          : <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                        }
                        <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap pt-0.5">
                          {/* eslint-disable-next-line i18next/no-literal-string */}
                          {entry.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium uppercase whitespace-nowrap', LEVEL_COLORS[entry.level])}>
                          {entry.level}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {entry.labels['app'] || entry.labels['container'] || ''}
                        </span>
                        <span className="text-sm text-slate-800 dark:text-slate-200 font-mono break-all flex-1 truncate">
                          {entry.line.length > 200 ? entry.line.slice(0, 200) + '...' : entry.line}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-3 pt-1 bg-slate-50/50 dark:bg-slate-800/30">
                        <pre className="text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-all mb-3 max-h-48 overflow-y-auto">
                          {entry.line}
                        </pre>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {Object.entries(entry.labels).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px]">
                              {`${k}=${v}`}
                            </Badge>
                          ))}
                        </div>
                        {(entry.level === 'error' || entry.level === 'warn') && (
                          <Button
                            variant="outline"
                            className="text-xs h-7"
                            onClick={e => {
                              e.stopPropagation()
                              setAnalyzeEntry(entry)
                            }}
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            {t('observability.analyzeError', 'Analyze')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <LogAnalysisDialog
        entry={analyzeEntry}
        open={analyzeEntry !== null}
        onOpenChange={open => { if (!open) setAnalyzeEntry(null) }}
      />
    </div>
  )
}
